type Counts<K extends string> = Record<K, number>;
export type OperationsSummary = {
  scope: { kind: 'site'; tenantId: 'kop'; siteId: string };
  day: string;
  month: string;
  generatedAt: string;
  aggregation: 'daily' | 'monthly';
  risk: (Counts<'total' | 'draft' | 'registered' | 'unassigned'> & { complete: true }) | null;
  education: Counts<'total' | 'registered' | 'attendance'> | null;
  drill: Counts<'planned' | 'completed' | 'registered'> | null;
  work: Counts<'total' | 'monthly' | 'active' | 'suspended' | 'closed' | 'preparing'> | null;
  tbm:
    (Counts<'count' | 'companies'> & { attendance: number | null; turnout: number | null }) | null;
  inspection: Counts<
    'daily' | 'monthly' | 'registered' | 'unresolved' | 'approval' | 'reassign' | 'completed'
  > | null;
};
const object = (v: unknown): Record<string, unknown> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw Error('DASHBOARD_RESPONSE_INVALID');
  return v as Record<string, unknown>;
};
export function parseOperationsSummary(
  value: unknown,
  day: string,
  month: string,
  aggregation: OperationsSummary['aggregation'] = 'monthly',
) {
  const result = object(value),
    scope = object(result.scope);
  if (
    scope.kind !== 'site' ||
    scope.tenantId !== 'kop' ||
    typeof scope.siteId !== 'string' ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(scope.siteId) ||
    result.day !== day ||
    result.month !== month ||
    result.aggregation !== aggregation ||
    typeof result.generatedAt !== 'string' ||
    !Number.isFinite(Date.parse(result.generatedAt))
  )
    throw Error('DASHBOARD_PERIOD_MISMATCH');
  const groups = {
    risk: ['total', 'draft', 'registered', 'unassigned'],
    education: ['total', 'registered', 'attendance'],
    drill: ['planned', 'completed', 'registered'],
    work: ['total', 'monthly', 'active', 'suspended', 'closed', 'preparing'],
    tbm: ['count', 'companies', 'attendance', 'turnout'],
    inspection: [
      'daily',
      'monthly',
      'registered',
      'unresolved',
      'approval',
      'reassign',
      'completed',
    ],
  };
  for (const [key, fields] of Object.entries(groups)) {
    if (result[key] === null) continue;
    const group = object(result[key]);
    for (const field of fields) {
      if (key === 'tbm' && ['attendance', 'turnout'].includes(field) && group[field] === null)
        continue;
      if (
        typeof group[field] !== 'number' ||
        !Number.isSafeInteger(group[field]) ||
        Number(group[field]) < 0
      )
        throw Error('DASHBOARD_COUNT_INVALID');
    }
    if (key === 'risk' && group.complete !== true) throw Error('DASHBOARD_INCOMPLETE');
  }
  return result as OperationsSummary;
}
export async function loadOperationsSummary(input: {
  siteId: string;
  day: string;
  month: string;
  features: readonly string[];
  fresh?: boolean;
  aggregation?: OperationsSummary['aggregation'];
}) {
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(input.siteId))
    throw Error('DASHBOARD_SITE_REQUIRED');
  const features: Record<string, string> = {
    'regular-risk': 'risk',
    'safety-training': 'education',
    'evacuation-drill': 'drill',
    'work-permit': 'work',
    tbm: 'tbm',
    'inspection-reporting': 'inspection',
  };
  const modules = input.features.flatMap((id) => (features[id] ? [features[id]] : []));
  const { createInspectionDashboardAnalyticsReadPort } =
    await import('@kop/inspection-firebase-adapter');
  const aggregation = input.aggregation ?? 'monthly';
  const result = await createInspectionDashboardAnalyticsReadPort(
    'kop-safety-51efb',
  ).loadOperations({
    tenantId: 'kop',
    siteId: input.siteId,
    // The established server default is the daily dashboard aggregate. Send the
    // explicit value only for the monthly meeting read, whose contract predates it.
    ...(aggregation === 'monthly' ? { aggregation } : {}),
    day: input.day,
    month: input.month,
    modules,
    fresh: input.fresh === true,
  });
  const parsed = parseOperationsSummary(result, input.day, input.month, aggregation);
  if (parsed.scope.siteId !== input.siteId) throw Error('DASHBOARD_SITE_MISMATCH');
  return parsed;
}
